import React from 'react';
import { useField } from 'formik';
import { ErrorMessage, Text } from '@adc/parallax-component-library';

import './TimeInputWithError.scss';

type Props = {
  name: string;
  label: string | null;
  disabled?: boolean;
  testID?: string;
  supportingText?: string;
};

/**
 *
 * @param {string} name The name of the input field in the Formik config
 * @param {string | null} label The label for the input field
 * @param {boolean} disabled
 * @param {string} testID
 * @param {string} supportingText Displayed below the input field when no error is present
 * @returns {React.ReactElement} The TimeInputWithError component
 */
const TimeInputWithError = ({
  name,
  label,
  disabled = false,
  testID,
  supportingText,
}: Props) => {
  const [field, meta] = useField<string>(name);
  const inputId = name;
  const errorId = `${inputId}-error`;
  const supportingTextId = `${inputId}-supporting-text`;
  const hasError = !!(meta.touched && meta.error && !disabled);
  const describedBy = hasError
    ? errorId
    : supportingText
      ? supportingTextId
      : undefined;

  return (
    <div className="time-input-with-error">
      <Text color="$text.60" type="$bodySmall.default">
        {label ? label : null}
        <input
          {...field}
          value={field.value ?? ''}
          aria-label={label || undefined}
          aria-describedby={describedBy || undefined}
          aria-invalid={hasError || undefined}
          className={[
            'time-input-with-error__input',
            hasError ? 'time-input-with-error__input--error' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          data-testid={testID}
          disabled={disabled}
          id={inputId}
          type="time"
          onKeyDown={(e) => {
            // Prevent the form from submitting when pressing Enter in the time input field
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        />
      </Text>

      {hasError && (
        <div id={errorId} role="alert">
          <ErrorMessage message={meta.error as string} testID={testID} />
        </div>
      )}

      {!hasError && supportingText && (
        <Text
          color="$text.60"
          type="$bodySmall.default"
          id={supportingTextId}
          testID={`${testID}-supporting-text`}
        >
          {supportingText}
        </Text>
      )}
    </div>
  );
};

export default TimeInputWithError;
